import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { Observable, of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import {
  OperationType,
  Portfolio,
  SettlementType,
  TicketStatus,
  TicketType
} from "../../model/domain/Entity";
import { PublicFixedIncome } from "../../model/domain/PublicFixedIncome";
import { PublicSecurity } from "../../model/domain/PublicSecurity";
import { StockFixedIncomePosition } from "../../model/domain/StockFixedIncomeModel";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-selic",
  templateUrl: "./selic.component.html",
  styleUrls: ["./selic.component.css"],
})
export class SelicComponent implements OnInit, OnDestroy {
  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;

  dataChanged: EventEmitter<string>;

  public model: PublicFixedIncome = null;

  faInfoCircle = faInfoCircle;

  navigationSubscription;

  canValidate = false;
  isEditing = false;
  formEnabled = true;

  TiposCompra: NamedType<number>[];
  TiposLiquidacao: NamedType<number>[];

  publicSecurityTypes: string[];
  selectedPublicSecurityType: string = null;

  publicSecurityIdsList: string[] = null;
  selectedPublicSecurityId: string = null;

  publicSecurityListResult: PublicSecurity[] = null;
  publicSecurityList: PublicSecurity[] = null;
  selectedPublicSecurityItem: PublicSecurity = null;
  isNotProduction = environment.name != 'production';

  constructor(
    private service: TicketService,
    public validator: ValidatorService,
    private dialogService: DialogService,
    private authService: AuthService,
    private snackBarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private decimalPipe: DecimalPipe,
    private location: Location,
    private loaderService: LoaderService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  validators = {
    operationTypeId: (value) =>
      [OperationType.Application, OperationType.Redemption].includes(value),
    operationValue: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.currencyMask, {}),
    amount: (value) => value > 0,
    unitPrice: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.currencyMask, {}),
    settlementDate: (value) =>
      this.model.settlementTypeId == SettlementType.Term
        ? this.coalesce(value).length > 0
        : true,
    acquisitionDate: (value) =>
      this.model.operationTypeId == OperationType.Redemption
        ? this.coalesce(value).length > 0
        : true,
    settlementTypeId: (value) =>
      [SettlementType.Term, SettlementType.InCash].includes(value),
    expirationDate: (value) => this.coalesce(value).length > 0,
    security: (value) => this.coalesce(value).length > 0,
    securityId: (value) => this.coalesce(value).length > 0,
  };

  ngOnInit() {
    this.dataChanged = new EventEmitter<string>();

    this.service.getPublicSecurityTypes().subscribe((p) => {
      this.publicSecurityTypes = p;
    });

    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new PublicFixedIncome();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.selectedPublicSecurityType = null;
    this.model.securityId = null;

    this.isEditing = false;
    this.canValidate = false;
    this.formEnabled = true;
    this.selectedPublicSecurityItem = null;
    this.model.id = 0;
    this.model.command = "0";
    this.model.operationTypeId = OperationType.Redemption;

    this.model.operationDate = new Date();
    this.clearPublicSecurityItem();

    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }
  setEnable(enabled: boolean = false) {
    this.formEnabled = enabled;
    setTimeout(() => {
      this.fundoComponent.setEnable(true);
      if (enabled) {
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null): void {
    this.model = new PublicFixedIncome();
    this.TiposCompra = this.service.ListStandardOperationTypes();
    this.TiposLiquidacao = this.service.ListSelicSettlementTypes();
    this.model.operationTypeId = OperationType.Application;
    this.model.settlementTypeId = SettlementType.InCash;

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;
    this.model.command = "0";

    if (id) {
      this.service.PublicFixedIncome.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );

          if (this.model.counterpartDocument) {
            this.model.counterpartDocument = this.validator.formatDocument(
              this.model.counterpartDocument
            );
          }

          this.model.operationValue = this.decimalPipe.transform(
            this.model.operationValue,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.amount = this.decimalPipe.transform(
            this.model.amount,
            "1.0-0",
            "pt-BR"
          ) as any;

          this.model.unitPrice = this.decimalPipe.transform(
            this.model.unitPrice,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.acquisitionDate = this.datePipe.transform(
            this.model.acquisitionDate,
            "yyyy-MM-dd"
          ) as any;

          this.model.settlementDate = this.datePipe.transform(
            this.model.settlementDate,
            "dd/MM/yyyy"
          ) as any;

          this.model.expirationDate = this.datePipe.transform(
            this.model.expirationDate,
            "dd/MM/yyyy"
          ) as any;

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.counterpartComponent.fillComponentFieldsLoad(this.model);

          this.selectedPublicSecurityType = this.model.security;

          this.service
            .getPublicSecurity(this.selectedPublicSecurityType)
            .subscribe((res) => {
              this.publicSecurityList = res;

              this.publicSecurityList.forEach((x) => {
                x.expirationDate = this.datePipe.transform(
                  x.expirationDate,
                  "dd/MM/yyyy",
                  "UTC/GMT"
                ) as any;
                x.issueDate = this.datePipe.transform(
                  x.issueDate,
                  "dd/MM/yyyy"
                ) as any;
              });

              this.selectedPublicSecurityItem = this.publicSecurityList.filter(
                (p) => p.expirationDate === this.model.expirationDate
              )[0];
            });

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.PublicFixedIncome])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  submit(): void {
    this.loaderService.addLoader();
    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(
    f: NgForm,
    duplicate: boolean = false
  ): Observable<PublicFixedIncome> {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    if (f.invalid || errors.length > 0 || fundErrors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      return throwError("Verifique os campos nos dados da Contraparte.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.amount = this.validator.getCurrency(data.amount);
    data.operationValue = this.validator.getCurrency(data.operationValue);
    data.unitPrice = this.validator.getCurrency(data.unitPrice);
    data.counterpartDocument = this.validator.sanitize(
      data.counterpartDocument
    );

    if (data.acquisitionDate != null) {
      data.acquisitionDate = new Date(
        data.acquisitionDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.settlementDate != null) {
      data.settlementDate = new Date(
        data.settlementDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.expirationDate != null) {
      data.expirationDate = new Date(
        data.expirationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.issueDate != null) {
      data.issueDate = new Date(
        data.issueDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.PublicFixedIncome.updateTicket(this.model.id, data)
        : this.service.PublicFixedIncome.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((fixedIncome: PublicFixedIncome) => {
          if (!editing) {
            this.model.id = fixedIncome.id;
            this.model.operationDate = fixedIncome.operationDate;
            this.model.statusId = fixedIncome.statusId;
            this.model.accountManagerName = fixedIncome.accountManagerName;
            this.model.accountManagerDocument =
              fixedIncome.accountManagerDocument;
            this.model.ticketCutoffTypeId = fixedIncome.ticketCutoffTypeId;
            this.model.portfolioFundId = fixedIncome.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (fixedIncome: PublicFixedIncome) => {
            this.service.PublicFixedIncome.forwardTicket(
              fixedIncome.id
            ).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(fixedIncome.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.PublicFixedIncome.cancelDraftTicket(
              this.model.id
            );
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve(): void {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service.approve(this.model.id, "").subscribe(() => {
            this.snackBarService.open("Boleta atualizada com sucesso.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.getTicket(this.model.id);
          });
        }
      });
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );

              this.getTicket(this.model.id);
            });
        }
      });
  }

  calculateOperationValue(): void {
    const amount = this.validator.getCurrency(this.model.amount);
    const unitPrice = this.validator.getCurrency(this.model.unitPrice);
    const operationValue = amount * unitPrice;
    this.model.operationValue = this.transformDecimal(operationValue);
  }

  isOperationValueModified(): boolean {
    const amount = this.validator.getCurrency(this.model.amount);
    const unitPrice = this.validator.getCurrency(this.model.unitPrice);
    const operationValue = amount * unitPrice;

    return (
      this.validator.getCurrency(this.model.operationValue) !==
      this.validator.getCurrency(this.transformDecimal(operationValue))
    );
  }

  transformDecimal(val: number): number {
    return this.decimalPipe.transform(val, "1.0-8", "pt-BR") as any;
  }

  duplicate(): void {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.SELIC },
          ]);
        });
    });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string) {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  isTermo() {
    return this.model.settlementTypeId === SettlementType.Term;
  }

  isSell() {
    return this.model.operationTypeId === OperationType.Redemption;
  }

  isApplication() {
    return this.model.operationTypeId === OperationType.Application;
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (this.model.id === 0) {
      cutoffTypeId = TicketCutoffTypeEnum.PublicFixedIncome;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketFundQuota) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketFundQuota)
    );
  }

  publicSecurityTypeSelected() {
    if (this.selectedPublicSecurityType) {
      this.model.security = this.selectedPublicSecurityType;

      this.clearPublicSecurityItem();

      this.service
        .getPublicSecurity(this.selectedPublicSecurityType)
        .subscribe((res) => {
          this.publicSecurityList = this.publicSecurityListResult = res;

          const securityIdList = res
            .map((item) => item.securityId)
            .filter((value, index, self) => self.indexOf(value) === index);

          this.publicSecurityIdsList = securityIdList;

          if (securityIdList.length > 1 && this.model.securityId == '') {
            this.model.securityId = null;
          } else if (securityIdList.length === 1) {
            this.model.securityId = securityIdList[0];
          }

          this.publicSecurityList.forEach((x) => {
            x.expirationDate = this.datePipe.transform(
              x.expirationDate,
              "dd/MM/yyyy",
              "UTC/GMT"
            ) as any;
            x.issueDate = this.datePipe.transform(
              x.issueDate,
              "dd/MM/yyyy"
            ) as any;
          });
        });
    } else {
      this.model.security = null;
      this.clearPublicSecurityItem();
    }
  }

  publicSecurityIdSelected() {
    this.clearPublicSecurityItem();

    const filteredPublicSecurityList = this.publicSecurityListResult.filter(
      (x) => x.securityId === this.model.securityId
    );
    this.publicSecurityList = filteredPublicSecurityList;

    if (filteredPublicSecurityList.length === 1) {
      this.selectedPublicSecurityItem = filteredPublicSecurityList[0];
      this.publicSecurityItemSelected();
    }
  }

  publicSecurityItemSelected() {
    if (this.selectedPublicSecurityItem) {
      this.model.securitySourceId =
        this.selectedPublicSecurityItem.securitySourceId;
      this.model.expirationDate =
        this.selectedPublicSecurityItem.expirationDate;
      this.model.issueDate = this.selectedPublicSecurityItem.issueDate;
    } else {
      this.clearPublicSecurityItem();
    }
  }

  clearPublicSecurityItem() {
    this.model.securitySourceId = null;
    this.model.expirationDate = null;
    this.model.issueDate = null;

    this.selectedPublicSecurityItem = null;
  }

  clearPublicSecurityFields() {
    this.model.security = null;
    this.model.securityId = null;
    this.model.expirationDate = null;

    this.publicSecurityIdsList = null;
    this.selectedPublicSecurityType = null;

    this.clearPublicSecurityItem();
  }

  getStockSelectedFixedIncome(selectedFixedIncome: StockFixedIncomePosition) {
    if (this.isSell()) {
      let publicSecurityType =
        selectedFixedIncome.code +
        " - " +
        (selectedFixedIncome.selicCode ?? "000000");

      this.service.getPublicSecurity(publicSecurityType).subscribe((res) => {
        this.publicSecurityList = res;

        this.publicSecurityList.forEach((x) => {
          x.expirationDate = this.datePipe.transform(
            x.expirationDate,
            "dd/MM/yyyy",
            "UTC/GMT"
          ) as any;
          x.issueDate = this.datePipe.transform(
            x.issueDate,
            "dd/MM/yyyy",
            "UTC/GMT"
          ) as any;
        });

        this.selectedPublicSecurityType = publicSecurityType;

        

        this.publicSecurityTypeSelected();

        let expirationDate = this.datePipe.transform(
          selectedFixedIncome.dueDate,
          "dd/MM/yyyy",
          "UTC/GMT"
        ) as any;

        this.selectedPublicSecurityItem = this.publicSecurityList.filter(
          (p) => p.expirationDate === expirationDate
        )[0];

        this.publicSecurityItemSelected();
      });

      this.model.securityId = selectedFixedIncome.code;

      this.model.acquisitionDate = this.datePipe.transform(
        selectedFixedIncome.purchaseDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;

      this.model.operationValue = this.decimalPipe.transform(
        selectedFixedIncome.netValue,
        "1.0-2",
        "pt-BR"
      ) as any;

      this.model.unitPrice = this.decimalPipe.transform(
        selectedFixedIncome.pu,
        "1.0-8",
        "pt-BR"
      ) as any;

      this.model.amount = selectedFixedIncome.quantity;
    }
  }
}
